import { useState, useRef, useCallback } from "react";
import api from "../../../lib/api";
import { 
  Search, Upload, FileText, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ChevronDown, ChevronUp, Download, X, Plus
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Switch } from "../../ui/switch";
import { Label } from "../../ui/label";
import { 
  Collapsible, CollapsibleContent, CollapsibleTrigger 
} from "../../ui/collapsible";
import { toast } from "sonner";
import { useOrganizationScope } from "../../../contexts/OrganizationScopeContext";

export const DocumentReviewCard = ({
  missionId,
  reviewFile,
  setReviewFile,
  reviewing,
  setReviewing,
  reviewResult,
  setReviewResult,
  reviewDocType,
  setReviewDocType,
  detectedType,
  setDetectedType,
  useMissionContext,
  setUseMissionContext,
  user,
  language = "cs"
}) => {
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  // Local states
  const [detectingType, setDetectingType] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [reviewProgressStep, setReviewProgressStep] = useState(0);
  const [expandedSections, setExpandedSections] = useState({ 
    compliant: false, 
    missing: true, 
    recommendations: true 
  });
  const [showAllItems, setShowAllItems] = useState({
    compliant: false,
    missing: false,
    recommendations: false
  });
  
  // Multi-document states
  const [secondReviewFile, setSecondReviewFile] = useState(null);
  const [secondDocType, setSecondDocType] = useState("auto");
  const [secondDetectedType, setSecondDetectedType] = useState(null);
  const [detectingSecondType, setDetectingSecondType] = useState(false);
  const [crossRefWarning, setCrossRefWarning] = useState(null);
  
  const fileInputRef = useRef(null);
  const secondFileInputRef = useRef(null);
  
  // Translations
  const t = language === 'cs' ? {
    title: "Review dokumentu",
    dropHere: "Přetáhněte ConOps nebo OM sem",
    orClick: "nebo klikněte pro výběr",
    selectFile: "Vybrat soubor",
    supportedFormats: "Podporované formáty: .pdf, .docx",
    detecting: "Detekuji typ dokumentu...",
    detected: "Detekován",
    confidence: "jistota",
    conops: "ConOps",
    om: "Operations Manual",
    combined: "ConOps + OM",
    other: "Jiný",
    auto: "Auto",
    unknown: "Neznámý typ",
    docType: "Typ dokumentu",
    document1: "Dokument 1",
    document2: "Dokument 2",
    remove: "Odebrat",
    addCompanion: "Přidat",
    crossRefWarning: "Dokument odkazuje na",
    missionContext: "Použít kontext mise",
    missionContextHelp: "Review bude přizpůsoben konkrétní misi",
    startReview: "Spustit review",
    reviewing: "Provádím review...",
    progress1: "Načítám dokument...",
    progress2: "Analyzuji strukturu...",
    progress3: "Kontroluji SORA 2.5 požadavky...",
    progress4: "Generuji doporučení...",
    reviewComplete: "Review dokončen",
    score: "Skóre",
    verdict: "Výsledek",
    compliant: "Vyhovující položky",
    missing: "Chybějící položky",
    recommendations: "Doporučení",
    downloadReport: "Stáhnout report",
    newReview: "Nový review",
    needsRevision: "Vyžaduje revizi",
    needsReview: "Vyžaduje kontrolu",
    approved: "Schváleno",
    rejected: "Zamítnuto",
    reviewError: "Chyba review",
    featureDisabled: "Funkce vypnuta",
    noVerdict: "Bez výsledku",
    multiDocReview: "Multi-dokument review",
    showAll: "Zobrazit vše",
    showLess: "Zobrazit méně"
  } : {
    title: "Document Review",
    dropHere: "Drop ConOps or OM here",
    orClick: "or click to select",
    selectFile: "Select file",
    supportedFormats: "Supported formats: .pdf, .docx",
    detecting: "Detecting document type...",
    detected: "Detected",
    confidence: "confidence",
    conops: "ConOps",
    om: "Operations Manual",
    combined: "ConOps + OM",
    other: "Other",
    auto: "Auto",
    unknown: "Unknown type",
    docType: "Document type",
    document1: "Document 1",
    document2: "Document 2",
    remove: "Remove",
    addCompanion: "Add",
    crossRefWarning: "Document references",
    missionContext: "Use mission context",
    missionContextHelp: "Review will be tailored to this specific mission",
    startReview: "Start Review",
    reviewing: "Reviewing...",
    progress1: "Loading document...",
    progress2: "Analyzing structure...",
    progress3: "Checking SORA 2.5 requirements...",
    progress4: "Generating recommendations...",
    reviewComplete: "Review Complete",
    score: "Score",
    verdict: "Result",
    compliant: "Compliant Items",
    missing: "Missing Items",
    recommendations: "Recommendations",
    downloadReport: "Download Report",
    newReview: "New Review",
    needsRevision: "Needs Revision",
    needsReview: "Needs Review",
    approved: "Approved",
    rejected: "Rejected",
    reviewError: "Review error",
    featureDisabled: "Feature disabled",
    noVerdict: "No result",
    multiDocReview: "Multi-document review",
    showAll: "Show all",
    showLess: "Show less"
  };
  
  // Read file as base64
  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  
  // Handle first file selection with cross-references detection
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    
    setReviewFile(file);
    setReviewResult(null);
    setDetectedType(null);
    setDetectingType(true);
    setCrossRefWarning(null);
    
    try {
      const base64 = await readFileAsBase64(file);
      
      // Call cross-references endpoint (includes detection + cross-ref analysis)
      const response = await api.post(
        '/compliance/review/cross-references',
        {
          file_content_base64: base64,
          filename: file.name,
          doc_type: "auto",
          org_id: scopedOrganizationId
        }
      );
      
      setDetectedType({
        detected_type: response.data.document_type,
        confidence: response.data.confidence,
        is_combined: response.data.is_combined || false
      });
      
      // Auto-set the doc type based on detection
      setReviewDocType(response.data.document_type);
      
      // Check if cross-references suggest a companion document
      if (response.data.needs_companion) {
        setCrossRefWarning({
          type: response.data.companion_type,
          count: response.data.companion_type === 'conops' 
            ? response.data.cross_references?.total_conops_refs 
            : response.data.cross_references?.total_om_refs,
          refs: response.data.companion_type === 'conops'
            ? response.data.cross_references?.conops_refs
            : response.data.cross_references?.om_refs
        });
      }
    } catch (err) {
      console.error("Detection failed:", err);
      setDetectedType(null);
      setReviewDocType("auto");
    } finally {
      setDetectingType(false);
    }
  }, [setReviewFile, setReviewResult, setDetectedType, setReviewDocType, scopedOrganizationId]);
  
  // Handle second file selection for multi-document review
  const handleSecondFileSelect = useCallback(async (file) => {
    if (!file) return;
    
    setSecondReviewFile(file);
    setSecondDetectedType(null);
    setDetectingSecondType(true);
    
    try {
      const base64 = await readFileAsBase64(file);
      
      const response = await api.post(
        '/compliance/review/cross-references',
        {
          file_content_base64: base64,
          filename: file.name,
          doc_type: "auto",
          org_id: scopedOrganizationId
        }
      );
      
      setSecondDetectedType({
        detected_type: response.data.document_type,
        confidence: response.data.confidence
      });
      setSecondDocType(response.data.document_type);
      
      // Clear cross-ref warning if we now have the companion
      if (crossRefWarning && response.data.document_type === crossRefWarning.type) {
        setCrossRefWarning(null);
      }
    } catch (err) {
      console.error("Second file detection failed:", err);
      setSecondDetectedType(null);
    } finally {
      setDetectingSecondType(false);
    }
  }, [scopedOrganizationId, crossRefWarning]);
  
  // Remove second document
  const removeSecondDocument = () => {
    setSecondReviewFile(null);
    setSecondDocType("auto");
    setSecondDetectedType(null);
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };
  
  // Poll for review result
  const pollForReviewResult = useCallback(async (reviewId, progressInterval) => {
    const maxPolls = 60;
    let pollCount = 0;
    
    while (pollCount < maxPolls) {
      pollCount++;
      
      try {
        const pollResult = await api.get(`/compliance/review/${reviewId}`);
        
        const status = pollResult.data.status;
        
        if (status === "completed") {
          clearInterval(progressInterval);
          setReviewResult(pollResult.data);
          setReviewing(false);
          setReviewProgressStep(0);
          toast.success(language === "cs" ? "Review dokončen" : "Review completed");
          return;
        } else if (status === "failed") {
          clearInterval(progressInterval);
          setReviewing(false);
          setReviewProgressStep(0);
          toast.error(pollResult.data.error_message || (language === "cs" ? "Review selhal" : "Review failed"));
          return;
        }
        
        await new Promise(r => setTimeout(r, 2000));
      } catch (pollErr) {
        console.error("Poll error:", pollErr);
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    clearInterval(progressInterval);
    setReviewing(false);
    setReviewProgressStep(0);
    toast.error(language === "cs" ? "Review vypršel časový limit" : "Review timed out");
  }, [setReviewResult, setReviewing, language]);
  
  // Submit review (single or multi-document)
  const submitReview = useCallback(async () => {
    if (!reviewFile || !scopedOrganizationId) return;
    
    setReviewing(true);
    setReviewResult(null);
    setReviewProgressStep(0);
    
    const progressInterval = setInterval(() => {
      setReviewProgressStep(prev => Math.min(prev + 1, 3));
    }, 2000);
    
    try {
      const isMultiDoc = secondReviewFile !== null;
      const base64_1 = await readFileAsBase64(reviewFile);
      
      if (isMultiDoc) {
        // Multi-document review
        const base64_2 = await readFileAsBase64(secondReviewFile);
        
        const response = await api.post(
          '/compliance/review/multi',
          {
            documents: [
              {
                file_content_base64: base64_1,
                filename: reviewFile.name,
                doc_type: reviewDocType,
                language
              },
              {
                file_content_base64: base64_2,
                filename: secondReviewFile.name,
                doc_type: secondDocType,
                language
              }
            ],
            org_id: scopedOrganizationId,
            mission_id: useMissionContext && missionId ? missionId : null,
            language
          }
        );
        
        await pollForReviewResult(response.data.review_id, progressInterval);
      } else {
        // Single document review
        const response = await api.post(
          '/compliance/review',
          {
            file_content_base64: base64_1,
            filename: reviewFile.name,
            doc_type: reviewDocType,
            org_id: scopedOrganizationId,
            mission_id: useMissionContext && missionId ? missionId : null,
            language
          }
        );
        
        await pollForReviewResult(response.data.review_id, progressInterval);
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error("Review failed:", err);
      setReviewing(false);
      setReviewProgressStep(0);
      toast.error(err.response?.data?.detail || (language === "cs" ? "Review selhal" : "Review failed"));
    }
  }, [reviewFile, secondReviewFile, reviewDocType, secondDocType, useMissionContext, missionId, scopedOrganizationId, setReviewing, setReviewResult, pollForReviewResult, language]);
  
  // Download review report
  const downloadReport = useCallback(() => {
    if (!reviewResult) return;
    const score = reviewResult.overall_score ?? reviewResult.score ?? 'N/A';
    const lines = [];
    lines.push(`# CheckMate Review Report`);
    lines.push(`Datum: ${new Date().toLocaleDateString('cs-CZ')}`);
    lines.push(`Skóre: ${score}%`);
    lines.push(`Verdikt: ${reviewResult.verdict ?? reviewResult.overall_status ?? 'N/A'}`);
    lines.push(`\n## Vyhovující položky (${reviewResult.compliant?.length || 0})`);
    (reviewResult.compliant || []).forEach(item => {
      lines.push(`• ${typeof item === 'string' ? item : item.item}`);
    });
    lines.push(`\n## Chybějící položky (${reviewResult.missing?.length || 0})`);
    (reviewResult.missing || []).forEach(item => {
      lines.push(`• ${typeof item === 'string' ? item : item.item}`);
      if (item.reference) lines.push(`  Reference: ${item.reference}`);
    });
    lines.push(`\n## Doporučení (${reviewResult.recommendations?.length || 0})`);
    (reviewResult.recommendations || []).forEach(item => {
      lines.push(`• ${typeof item === 'string' ? item : (item.item || item.suggestion)}`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `checkmate_review_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [reviewResult]);
  
  // Reset review
  const resetReview = () => {
    setReviewFile(null);
    setReviewResult(null);
    setDetectedType(null);
    setReviewDocType("auto");
    setCrossRefWarning(null);
    setSecondReviewFile(null);
    setSecondDocType("auto");
    setSecondDetectedType(null);
  };
  
  // Badge of the review outcome. status only tracks polling (a finished review is always "completed"); the outcome
  // is verdict, or overall_status of a multi-document review.
  const getVerdictDisplay = (result) => {
    const verdict = result?.verdict ?? result?.overall_status ?? null;
    if (verdict === "approved") {
      return { color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30", label: t.approved };
    }
    if (verdict === "needs_revision") {
      return { color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30", label: t.needsRevision };
    }
    if (verdict === "needs_review") {
      return { color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30", label: t.needsReview };
    }
    if (verdict === "rejected") {
      return { color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30", label: t.rejected };
    }
    if (verdict === "error") {
      return { color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30", label: t.reviewError };
    }
    if (verdict === "feature_disabled") {
      return { color: "text-gray-600", bg: "bg-gray-100 dark:bg-gray-800", label: t.featureDisabled };
    }
    return { color: "text-gray-600", bg: "bg-gray-100 dark:bg-gray-800", label: t.noVerdict };
  };
  
  // Get score color
  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };
  
  // Get type color for dropdown/badge
  const getTypeColor = (type) => {
    if (type === "conops") return "bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300";
    if (type === "om") return "bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300";
    if (type === "combined") return "bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300";
    return "bg-gray-50 border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300";
  };
  
  // Progress messages
  const progressMessages = [t.progress1, t.progress2, t.progress3, t.progress4];
  
  // Toggle section
  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle show all items
  const toggleShowAll = (key) => {
    setShowAllItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="p-4 rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-sm" data-testid="document-review-card">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Search className="h-5 w-5 text-[#1E3A5F]" />
        <span className="font-medium text-[var(--text-primary)]">{t.title}</span>
        {secondReviewFile && (
          <Badge variant="outline" className="text-xs ml-auto">{t.multiDocReview}</Badge>
        )}
      </div>
      
      {/* Review in progress */}
      {reviewing && (
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{t.reviewing}</span>
          </div>
          <div className="space-y-1">
            {progressMessages.map((msg, idx) => (
              <div key={idx} className={`flex items-center gap-2 text-xs ${
                idx === reviewProgressStep ? "text-blue-700 dark:text-blue-300 font-medium" 
                : idx < reviewProgressStep ? "text-green-600" 
                : "text-blue-400"
              }`}>
                {idx < reviewProgressStep ? <CheckCircle2 className="h-3 w-3" /> 
                : idx === reviewProgressStep ? <Loader2 className="h-3 w-3 animate-spin" /> 
                : <span className="h-3 w-3 rounded-full border border-current" />}
                {msg}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Review Results - Inline Expandable */}
      {reviewResult && !reviewing && (
        <div className="space-y-3">
          {/* Score & Status Header */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)]">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-xs text-[var(--text-muted)]">{t.score}</span>
                <p className={`text-2xl font-bold ${getScoreColor(reviewResult.overall_score || reviewResult.score)}`}>
                  {reviewResult.overall_score ?? reviewResult.score ?? 'N/A'}%
                </p>
              </div>
              <div>
                <span className="text-xs text-[var(--text-muted)]">{t.verdict}</span>
                <Badge
                  className={`${getVerdictDisplay(reviewResult).bg} ${getVerdictDisplay(reviewResult).color} border-0 mt-1`}
                  data-testid="document-review-verdict"
                >
                  {getVerdictDisplay(reviewResult).label}
                </Badge>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadReport} className="h-7 text-xs gap-1">
                <Download className="h-3 w-3" /> {t.downloadReport}
              </Button>
              <Button variant="ghost" size="sm" onClick={resetReview} className="h-7 text-xs gap-1">
                <X className="h-3 w-3" /> {t.newReview}
              </Button>
            </div>
          </div>
          
          {/* Compliant Items */}
          {reviewResult.compliant?.length > 0 && (
            <Collapsible open={expandedSections.compliant} onOpenChange={() => toggleSection('compliant')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    {t.compliant} ({reviewResult.compliant.length})
                  </span>
                </div>
                {expandedSections.compliant ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-6">
                <div className={`space-y-1 ${showAllItems.compliant && reviewResult.compliant.length > 10 ? 'max-h-64 overflow-y-auto pr-2' : ''}`}>
                  {(showAllItems.compliant ? reviewResult.compliant : reviewResult.compliant.slice(0, 5)).map((item, i) => (
                    <p key={i} className="text-xs text-[var(--text-secondary)]">• {typeof item === 'string' ? item : item.item}</p>
                  ))}
                </div>
                {reviewResult.compliant.length > 5 && (
                  <button
                    onClick={() => toggleShowAll('compliant')}
                    className="mt-2 text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    {showAllItems.compliant ? t.showLess : `${t.showAll} (${reviewResult.compliant.length})`}
                  </button>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {/* Missing Items */}
          {reviewResult.missing?.length > 0 && (
            <Collapsible open={expandedSections.missing} onOpenChange={() => toggleSection('missing')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-300">
                    {t.missing} ({reviewResult.missing.length})
                  </span>
                </div>
                {expandedSections.missing ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-6">
                <div className={`space-y-1 ${showAllItems.missing && reviewResult.missing.length > 10 ? 'max-h-64 overflow-y-auto pr-2' : ''}`}>
                  {(showAllItems.missing ? reviewResult.missing : reviewResult.missing.slice(0, 5)).map((item, i) => (
                    <p key={i} className="text-xs text-[var(--text-secondary)]">• {typeof item === 'string' ? item : item.item}</p>
                  ))}
                </div>
                {reviewResult.missing.length > 5 && (
                  <button
                    onClick={() => toggleShowAll('missing')}
                    className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    {showAllItems.missing ? t.showLess : `${t.showAll} (${reviewResult.missing.length})`}
                  </button>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {/* Recommendations */}
          {reviewResult.recommendations?.length > 0 && (
            <Collapsible open={expandedSections.recommendations} onOpenChange={() => toggleSection('recommendations')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {t.recommendations} ({reviewResult.recommendations.length})
                  </span>
                </div>
                {expandedSections.recommendations ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 pl-6">
                <div className={`space-y-1 ${showAllItems.recommendations && reviewResult.recommendations.length > 10 ? 'max-h-64 overflow-y-auto pr-2' : ''}`}>
                  {(showAllItems.recommendations ? reviewResult.recommendations : reviewResult.recommendations.slice(0, 5)).map((item, i) => (
                    <p key={i} className="text-xs text-[var(--text-secondary)]">• {typeof item === 'string' ? item : (item.item || item.suggestion || item.recommendation || JSON.stringify(item))}</p>
                  ))}
                </div>
                {reviewResult.recommendations.length > 5 && (
                  <button
                    onClick={() => toggleShowAll('recommendations')}
                    className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    {showAllItems.recommendations ? t.showLess : `${t.showAll} (${reviewResult.recommendations.length})`}
                  </button>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}
      
      {/* File Upload / Selection */}
      {!reviewing && !reviewResult && (
        <>
          {/* Drop Zone - Only show if no file selected */}
          {!reviewFile && (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragActive 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                  : 'border-[var(--border-default)] hover:border-purple-300 hover:bg-[var(--bg-elevated)]'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.docx"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
              />
              <Upload className="h-8 w-8 mx-auto text-[var(--text-muted)] mb-2" />
              <p className="text-sm text-[var(--text-secondary)]">{t.dropHere}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t.orClick}</p>
              <p className="text-xs text-[var(--text-muted)] mt-2">{t.supportedFormats}</p>
            </div>
          )}
          
          {/* First Document Card */}
          {reviewFile && (
            <div className="bg-[var(--bg-secondary)] border border-[var(--card-border)] rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[var(--text-muted)]" />
                  <span className="text-sm text-[var(--text-primary)] font-medium">{t.document1}:</span>
                </div>
                <button
                  onClick={() => {
                    setReviewFile(null);
                    setDetectedType(null);
                    setReviewDocType("auto");
                    setCrossRefWarning(null);
                  }}
                  className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  {t.remove}
                </button>
              </div>
              <span className="text-sm text-[var(--text-primary)] truncate block mb-2">{reviewFile.name}</span>
              
              {detectingType && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t.detecting}
                </div>
              )}
              
              {!detectingType && (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Manual type selector dropdown */}
                  <select
                    value={reviewDocType}
                    onChange={(e) => setReviewDocType(e.target.value)}
                    className={`text-xs px-2 py-1 rounded border cursor-pointer ${getTypeColor(reviewDocType)}`}
                  >
                    <option value="conops">{t.conops}</option>
                    <option value="om">{t.om}</option>
                    <option value="combined">{t.combined}</option>
                    <option value="other">{t.other}</option>
                  </select>
                  {/* Show confidence if detected */}
                  {detectedType?.confidence && (
                    <span className="text-xs text-[var(--text-muted)]">
                      ({detectedType.confidence}% {t.confidence})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Second Document Card */}
          {secondReviewFile && (
            <div className="bg-[var(--bg-secondary)] border border-[var(--card-border)] rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[var(--text-muted)]" />
                  <span className="text-sm text-[var(--text-primary)] font-medium">{t.document2}:</span>
                </div>
                <button
                  onClick={removeSecondDocument}
                  className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  {t.remove}
                </button>
              </div>
              <span className="text-sm text-[var(--text-primary)] truncate block mb-2">{secondReviewFile.name}</span>
              
              {detectingSecondType && (
                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t.detecting}
                </div>
              )}
              
              {!detectingSecondType && (
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={secondDocType}
                    onChange={(e) => setSecondDocType(e.target.value)}
                    className={`text-xs px-2 py-1 rounded border cursor-pointer ${getTypeColor(secondDocType)}`}
                  >
                    <option value="conops">{t.conops}</option>
                    <option value="om">{t.om}</option>
                    <option value="combined">{t.combined}</option>
                    <option value="other">{t.other}</option>
                  </select>
                  {secondDetectedType?.confidence && (
                    <span className="text-xs text-[var(--text-muted)]">
                      ({secondDetectedType.confidence}% {t.confidence})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Hidden file input for second document */}
          <input
            type="file"
            ref={secondFileInputRef}
            className="hidden"
            accept=".pdf,.docx"
            onChange={(e) => e.target.files?.[0] && handleSecondFileSelect(e.target.files[0])}
          />
          
          {/* Cross-reference Warning - suggest adding companion */}
          {crossRefWarning && !secondReviewFile && reviewFile && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-700 dark:text-amber-300 font-medium">
                    {t.crossRefWarning} {crossRefWarning.type === 'conops' ? 'ConOps' : 'OM'} ({crossRefWarning.count}x)
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 gap-1 text-amber-700 border-amber-300 hover:bg-amber-100"
                    onClick={() => secondFileInputRef.current?.click()}
                  >
                    <Plus className="h-3 w-3" />
                    {t.addCompanion} {crossRefWarning.type === 'conops' ? 'ConOps' : 'OM'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Add second document button (if first exists and no cross-ref warning) */}
          {reviewFile && !secondReviewFile && !crossRefWarning && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mb-3 gap-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              onClick={() => secondFileInputRef.current?.click()}
            >
              <Plus className="h-3 w-3" />
              {language === 'cs' ? 'Přidat druhý dokument pro cross-reference review' : 'Add second document for cross-reference review'}
            </Button>
          )}
          
          {/* Options & Start */}
          {reviewFile && (
            <div className="space-y-3">
              {/* Mission context toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="mission-context" className="text-sm">{t.missionContext}</Label>
                  <p className="text-xs text-[var(--text-muted)]">{t.missionContextHelp}</p>
                </div>
                <Switch
                  id="mission-context"
                  checked={useMissionContext}
                  onCheckedChange={setUseMissionContext}
                />
              </div>
              
              {/* Start Review Button */}
              <Button 
                onClick={submitReview}
                disabled={!reviewFile || detectingType || detectingSecondType || reviewing}
                className="w-full gap-2 bg-[#1E3A5F] hover:bg-[#2a4a73]"
              >
                {reviewing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.reviewing}
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    {t.startReview}
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentReviewCard;
